import { Form, Formik } from 'formik'
import * as React from 'react'
import { ActivityFormActions } from './ActivityFormActions'
import Stack from '@mui/material/Stack'
import { ActivityFormHeader } from './ActivityFormHeader'
import { useHandlers, useInitValues, useValidations } from './hooks'
import { ENTITY_TYPES } from '@sales-center/common'
import type { ActivitiesQuantity } from '../../../Activity'
import type { ActivityForm as ActivityFormValues } from './typings'
import { ActivityFormFields } from './ActivityFormFields'
import type { OwnerOption } from './ActivityFormFields.types'
import type { ActivityPrefill } from './hooks/useInitValues'

type Props = {
  // SH-1428: in attach mode (calendar "Create Event") there is no pre-selected
  // entity — the Attach-To picker sets it on the Formik model — so entityId and
  // entityType are optional here.
  entityId?: number
  entityType?: ENTITY_TYPES
  ownerId: string | null
  activitiesQuantity?: ActivitiesQuantity
  builtin?: boolean
  salesTeamId: number
  owners?: OwnerOption[]
  ownerError?: string
  attachToMode?: boolean
  attachToSlot?: React.ReactNode
  /** SH-2133: seeds the date/time fields from a calendar grid click. */
  prefill?: ActivityPrefill
  onCreated?: () => void
  onCancel?: () => void
}

export function ActivityForm(props: Props) {
  const { entityId, entityType, ownerId, salesTeamId, owners, attachToMode } =
    props
  const { attachToSlot, onCreated, onCancel, prefill, ownerError } = props
  // entityType is a placeholder when no entity is pre-selected (attach mode);
  // the real one is read from the Formik model at submit time.
  const { handleSubmit } = useHandlers({
    entityId: entityId ?? 0,
    entityType: entityType ?? ENTITY_TYPES.Lead,
    activitiesQuantity: 'single',
    attachToMode,
    onSuccess: onCreated,
  })
  const initValues = useInitValues(ownerId, prefill, attachToMode)
  const { validate } = useValidations(attachToMode, ownerError)

  return (
    // Values are ActivityForm-shaped at rest; dates start null during entry and
    // are validated before submit. Pinning the generic keeps onSubmit/validate
    // (both typed to ActivityForm) bound without an `any` escape hatch.
    <Formik<ActivityFormValues>
      initialValues={initValues as unknown as ActivityFormValues}
      onSubmit={handleSubmit}
      validate={validate}
      validateOnMount={false}
    >
      <Form>
        <Stack width={attachToMode ? '100%' : 355} padding={2}>
          {!attachToMode && <ActivityFormHeader />}
          <ActivityFormFields
            entityType={entityType ?? ENTITY_TYPES.Lead}
            salesTeamId={salesTeamId}
            owners={owners}
            attachToMode={attachToMode}
            attachToSlot={attachToSlot}
          />
          <ActivityFormActions onCancelClick={onCancel} />
        </Stack>
      </Form>
    </Formik>
  )
}
